import { authenticatedFetch } from "./http";

export interface AccessPermission {
  id: string; code: string; resource: string; action: string;
  description?: string;
}
export interface AccessRole {
  id: string; code: string; displayName: string; description?: string;
  systemRole: boolean; status: string; version: number;
  permissionCodes: string[]; assignedUsers: number;
}
export interface AccessUser {
  id: string; username?: string; firstName?: string; lastName?: string;
  email: string; displayName: string; status: string;
  lastAuthenticatedAt?: string; disabledAt?: string; disabledReason?: string;
  externalSubject?: string; version: number; roleIds: string[];
}
export interface ActiveUserSession {
  sessionId: string; userId: string; username: string; ipAddress: string;
  startedAt: string; lastAccessAt: string;
  browserName?: string; browserVersion?: string; operatingSystem?: string;
  deviceType?: "DESKTOP" | "MOBILE" | "TABLET" | "PDA" | "UNKNOWN";
  deviceName?: string; deviceFirstSeenAt?: string; deviceLastSeenAt?: string;
}
export const loadAccessPermissions = () =>
  authenticatedFetch<AccessPermission[]>("/api/v1/access/permissions");
export const loadAccessRoles = () =>
  authenticatedFetch<AccessRole[]>("/api/v1/access/roles");
export const loadAccessUsers = () =>
  authenticatedFetch<AccessUser[]>("/api/v1/access/users");
export const loadActiveUserSessions = () =>
  authenticatedFetch<ActiveUserSession[]>("/api/v1/access/sessions");
export const recordSessionPresence = (payload: {
  userAgent: string; browserName: string; browserVersion: string;
  operatingSystem: string; deviceType: string; deviceName: string;
}) => authenticatedFetch<void>("/api/v1/access/presence", {
  method: "POST", body: JSON.stringify(payload)
});
export const createAccessRole = (payload: {
  code: string; displayName: string; description: string; permissionCodes: string[];
}) => authenticatedFetch<AccessRole>("/api/v1/access/roles", {
  method: "POST", body: JSON.stringify(payload)
});
export const updateAccessRole = (role: AccessRole, payload: {
  displayName: string; description: string; status: string; permissionCodes: string[];
}) => authenticatedFetch<AccessRole>(`/api/v1/access/roles/${role.id}`, {
  method: "PUT", body: JSON.stringify({ ...payload, version: role.version })
});
export const assignAccessRoles = (userId: string, roleIds: string[]) =>
  authenticatedFetch<AccessUser>(`/api/v1/access/users/${userId}/roles`, {
    method: "PUT", body: JSON.stringify({ roleIds })
  });
export const createAccessUser = (payload: {
  username: string; email: string; firstName: string; lastName: string;
  initialPassword: string; forcePasswordChange: boolean; roleIds: string[];
}) => authenticatedFetch<AccessUser>("/api/v1/access/users", {
  method: "POST", body: JSON.stringify(payload)
});
export const updateAccessUser = (user: AccessUser, payload: {
  email: string; firstName: string; lastName: string;
}) => authenticatedFetch<AccessUser>(`/api/v1/access/users/${user.id}`, {
  method: "PUT", body: JSON.stringify({ ...payload, version: user.version })
});
export const suspendAccessUser = (user: AccessUser, reason: string) =>
  authenticatedFetch<AccessUser>(`/api/v1/access/users/${user.id}/suspend`, {
    method: "POST", body: JSON.stringify({ version: user.version, reason })
  });
export const reactivateAccessUser = (user: AccessUser) =>
  authenticatedFetch<AccessUser>(`/api/v1/access/users/${user.id}/reactivate`, {
    method: "POST", body: JSON.stringify({ version: user.version })
  });
export const disableAccessUser = (user: AccessUser, reason: string) =>
  authenticatedFetch<AccessUser>(`/api/v1/access/users/${user.id}/disable`, {
    method: "POST", body: JSON.stringify({ version: user.version, reason })
  });
export const resetAccessUserPassword = (
  user: AccessUser, password: string, forceChange: boolean
) => authenticatedFetch<void>(`/api/v1/access/users/${user.id}/reset-password`, {
  method: "POST", body: JSON.stringify({
    version: user.version, password, forceChange
  })
});
